// PROJECT: IRON COMMAND — movement, formation & combat reference engine (prototype v0.2)
// Axial (q, r), pointy-top. Direction indices are clockwise from East (screen y grows downward).
// Every number tagged PLAYTEST is a tuning value, not a balance-tested target.

// ---------------------------------------------------------------- hex math
export const DIRS = [
  { q: 1, r: 0 },  // 0 East
  { q: 0, r: 1 },  // 1 Southeast
  { q: -1, r: 1 }, // 2 Southwest
  { q: -1, r: 0 }, // 3 West
  { q: 0, r: -1 }, // 4 Northwest
  { q: 1, r: -1 }, // 5 Northeast
];
const z = (n) => n || 0; // normalise -0
export const add = (a, b) => ({ q: a.q + b.q, r: a.r + b.r });
export const sub = (a, b) => ({ q: a.q - b.q, r: a.r - b.r });
export const scale = (a, k) => ({ q: a.q * k, r: a.r * k });
export const key = (h) => `${h.q},${h.r}`;
export const eq = (a, b) => a.q === b.q && a.r === b.r;
export const dist = (a, b) => { const d = sub(b, a); return (Math.abs(d.q) + Math.abs(d.r) + Math.abs(d.q + d.r)) / 2; };
export const rotCW = ({ q, r }) => ({ q: z(-r), r: z(q + r) });   // +60°
export const rotCCW = ({ q, r }) => ({ q: z(q + r), r: z(-q) });  // -60°
export const turnFacing = (f, rot) => (f + (rot === "cw" ? 1 : 5)) % 6;
export const neighbor = (h, d) => add(h, DIRS[((d % 6) + 6) % 6]);

export function ringClockwise(center, k) {
  const out = []; let h = add(center, scale(DIRS[4], k));
  for (let i = 0; i < 6; i++) for (let j = 0; j < k; j++) { out.push(h); h = add(h, DIRS[i]); }
  return out;
}
export function arcPath(pivot, pos, rot) {
  const k = dist(pivot, pos); if (k === 0) return [];
  const ring = ringClockwise(pivot, k), i = ring.findIndex((h) => eq(h, pos));
  const n = ring.length, s = rot === "cw" ? 1 : -1, path = [];
  for (let j = 1; j <= k; j++) path.push(ring[(((i + s * j) % n) + n) % n]);
  return path;
}
function cubeRound(x, y, zc) {
  let rx = Math.round(x), ry = Math.round(y), rz = Math.round(zc);
  const dx = Math.abs(rx - x), dy = Math.abs(ry - y), dz = Math.abs(rz - zc);
  if (dx > dy && dx > dz) rx = -ry - rz; else if (dy > dz) ry = -rx - rz;
  return { q: z(rx), r: z(ry) };
}
// Hexes strictly between a and b on the straight line (epsilon-nudged so edge cases resolve consistently).
export function lineBetween(a, b) {
  const n = dist(a, b), out = [], e = [1e-6, 2e-6, -3e-6];
  const A = [a.q + e[0], a.r + e[1], -a.q - a.r + e[2]], B = [b.q + e[0], b.r + e[1], -b.q - b.r + e[2]];
  for (let i = 1; i < n; i++) { const t = i / n; out.push(cubeRound(...A.map((v, k) => v + (B[k] - v) * t))); }
  return out;
}
const pixel = (h) => ({ x: Math.sqrt(3) * (h.q + h.r / 2), y: 1.5 * h.r });

// ---------------------------------------------------------------- data tables
export const TERRAIN = {
  plains:   { cost: 1,    control: "open",    cover: 0,  blocksLOS: false },
  ore:      { cost: 1,    control: "open",    cover: 0,  blocksLOS: false },
  ruins:    { cost: 2,    control: "broken",  cover: 30, blocksLOS: false }, // Fortified Ridge / Ruins
  mountain: { cost: null, control: "blocked", cover: 0,  blocksLOS: true },
  // Vertical traversal: a plateau is one level up and only joins the ground through a stairs/ladder link.
  plateau:  { cost: 1,    control: "open",    cover: 0,  blocksLOS: false, level: 1 },
  pit:      { cost: null, control: "blocked", cover: 0,  blocksLOS: false }, // spike pit: crossed only by jump pads
  // Open-field terrain for large generated maps. PLAYTEST.
  forest:   { cost: 2,    control: "broken",  cover: 20, blocksLOS: true },
  hills:    { cost: 2,    control: "open",    cover: 20, blocksLOS: false, vantage: 1 }, // half a level: fires down on flat ground
  water:    { cost: null, control: "blocked", cover: 0,  blocksLOS: false },
  ford:     { cost: 2,    control: "broken",  cover: 0,  blocksLOS: false },
  road:     { cost: 1,    control: "open",    cover: 0,  blocksLOS: false },
};
// Decision 1 (movement cap) is the `move` column. PLAYTEST.
// vsAir / vsGround / vsNaval: percent of attack that lands on that domain (default vsAir 0, vsNaval 0, vsGround 100).
// Air units fly over any terrain, ignore and exert no ZOC, ignore line of sight and terrain cover.
// Ships move only on water; land units never enter water.
export const UNITS = {
  infantry:  { cls: "infantry", hp: 30, atk: 15, range: [1, 1], indirect: false, armor: 0, move: 3, zoc: true, vsAir: 50, vsNaval: 0 },
  tank:      { cls: "armor",    hp: 70, atk: 35, range: [1, 2], indirect: false, armor: 5, move: 3, zoc: true, vsNaval: 100 },
  harvester: { cls: "armor",    hp: 50, atk: 0,  range: [0, 0], indirect: false, armor: 0, move: 2, zoc: false },
  artillery: { cls: "armor",    hp: 40, atk: 50, range: [3, 4], indirect: true,  armor: 0, move: 2, zoc: false, vsNaval: 100 },
  helicopter:{ cls: "air",      hp: 45, atk: 30, range: [1, 2], indirect: false, armor: 0, move: 5, zoc: false, air: true, vsAir: 50, vsNaval: 100 },
  jet:       { cls: "air",      hp: 40, atk: 45, range: [1, 3], indirect: false, armor: 0, move: 8, zoc: false, air: true, vsAir: 100, vsNaval: 100, sortie: true },
  gunboat:   { cls: "naval",    hp: 40, atk: 18, range: [1, 2], indirect: false, armor: 0, move: 5, zoc: false, naval: true, vsNaval: 100 },
  destroyer: { cls: "naval",    hp: 55, atk: 28, range: [1, 3], indirect: false, armor: 2, move: 4, zoc: false, naval: true, vsAir: 50, vsNaval: 100 },
  battleship:{ cls: "naval",    hp: 90, atk: 45, range: [2, 5], indirect: true,  armor: 8, move: 2, zoc: false, naval: true, vsNaval: 100 },
};
// Defensive structures: built in your territory, fire automatically at enemies that end a turn in range.
// Dock and coastal gun must sit on a coastal land hex (`coastal: true`).
export const STRUCTURES = {
  turret:  { hp: 80, atk: 25, range: [1, 3], armor: 5, vsAir: 0, vsNaval: 100, cost: 600, ap: 2, tech: 1 },
  aa:      { hp: 60, atk: 30, range: [1, 4], armor: 0, vsGround: 0, vsAir: 100, vsNaval: 0, cost: 700, ap: 2, tech: 2 },
  dock:    { hp: 70, atk: 0,  range: [0, 0], armor: 2, vsAir: 0, vsGround: 0, vsNaval: 0, cost: 800, ap: 2, tech: 1, coastal: true },
  coastal: { hp: 70, atk: 30, range: [1, 4], armor: 3, vsAir: 0, vsGround: 0, vsNaval: 100, cost: 650, ap: 2, tech: 1, coastal: true },
};
export const UNIT_DOMAIN = {
  infantry: "land", tank: "land", harvester: "land", artillery: "land",
  helicopter: "air", jet: "air",
  gunboat: "sea", destroyer: "sea", battleship: "sea",
};
// Map effects: ids in st.effects. Numbers are PLAYTEST. Applied only through the helpers below.
export const EFFECTS = {
  storm:       { id: "storm", name: "Storm", description: "Air attacks −25%. Jet sorties cost +1 AP.", airAtk: -25, sortieExtra: 1 },
  fog:         { id: "fog", name: "Fog", description: "Direct-fire range is capped at 2.", directRangeCap: 2 },
  "rich-veins":{ id: "rich-veins", name: "Rich veins", description: "Ore and harvester income ×1.5.", veinIncome: 1.5 },
  "rough-seas":{ id: "rough-seas", name: "Rough seas", description: "Ships −1 move. Naval attacks −15%.", shipMove: -1, navalAtk: -15 },
  "tech-race": { id: "tech-race", name: "Tech race", description: "Research costs −30%.", researchCost: 0.7 },
  fortified:   { id: "fortified", name: "Fortified", description: "Structures +50% HP.", structureHp: 1.5 },
};
export const RULES = {
  apPerTurn: 5, minGroup: 2, maxGroup: 5, maxWheelRadius: 2, attackCost: 1,
  baseIncome: 500, oreIncome: 100, harvesterIncome: 400, scavengerBonus: 100,
  baseCover: 50, angle: { front: 0, flank: 20, rear: 35 }, crossfire: 10,
  spearhead: { armored: 15, fireteam: 10 }, bracedFront: 10, bulwark: 15,
  maxTurns: 24, dropAp: 2, baseHp: 300,
  jumpAp: 2, highGround: 10, // PLAYTEST
  sortieAp: 2, techEdge: 15, maxTech: 3, researchAp: 2, researchCost: { 2: 1200, 3: 2000 }, buildRadius: 3, // PLAYTEST
};
// Links are engine-owned edges between two hexes. `extra` is AP on top of the destination's entry cost.
export const LINKS = {
  stairs: { extra: 1, types: ["infantry", "tank", "harvester", "artillery"] },
  ladder: { extra: 1, types: ["infantry"] },
  jump:   { types: ["infantry", "tank"] }, // flat RULES.jumpAp, ends the MOVE
};
export const UNIT_COSTS = { infantry: 300, tank: 800, harvester: 500, artillery: 1000, helicopter: 1200, jet: 1800, gunboat: 500, destroyer: 900, battleship: 1600 };
export const UNIT_AP_COST = { infantry: 1, tank: 2, harvester: 1, artillery: 3, helicopter: 2, jet: 3, gunboat: 1, destroyer: 2, battleship: 3 };
export const UNIT_TECH = { helicopter: 2, jet: 3, destroyer: 2, battleship: 3 }; // research tier needed to build; others need tier 1

// ---------------------------------------------------------------- state helpers
// Boards are a q×r parallelogram ({ qCount, rCount }) or an odd-row-offset rectangle ({ cols, rows }).
export function inBounds(st, h) {
  const b = st.board;
  if (b.cols !== undefined) {
    const col = h.q + Math.floor(h.r / 2);
    return h.r >= 0 && h.r < b.rows && col >= 0 && col < b.cols;
  }
  return h.q >= 0 && h.r >= 0 && h.q < b.qCount && h.r < b.rCount;
}
/** Every hex on the board, row by row. */
export function boardCells(st) {
  const b = st.board, out = [];
  if (b.cols !== undefined) {
    for (let r = 0; r < b.rows; r++) for (let c = 0; c < b.cols; c++) out.push({ q: c - Math.floor(r / 2), r });
  } else {
    for (let r = 0; r < b.rCount; r++) for (let q = 0; q < b.qCount; q++) out.push({ q, r });
  }
  return out;
}
/** Per-match rule override (st.rules) falling back to RULES. */
export const rule = (st, k) => st.rules?.[k] ?? RULES[k];
export const terrainId = (st, h) => st.tiles[key(h)] ?? "plains";
export const terrainAt = (st, h) => TERRAIN[terrainId(st, h)];
export const unitAt = (st, h) => st.units.find((u) => eq(u, h));
export const baseAt = (st, h) => (st.bases ?? []).find((b) => eq(b, h));
export const byId = (st, id) => st.units.find((u) => u.id === id);
export const structureAt = (st, h) => (st.structures ?? []).find((s) => eq(s, h));
export const structureById = (st, id) => (st.structures ?? []).find((s) => s.id === id);
export const techOf = (st, p) => st.players?.[p]?.tech ?? 1;
export const isAir = (u) => !!UNITS[u?.type]?.air;
export const isNaval = (u) => !!UNITS[u?.type]?.naval;
export const domainOf = (u) => (isAir(u) ? "air" : isNaval(u) ? "naval" : "ground");
export const hasEffect = (st, id) => (st.effects ?? []).includes(id);
export const domainAllowed = (st, domain) => !st.domains || st.domains.includes(domain);
const stats = (u) => UNITS[u.type];
export const vsAgainst = (s, target) => {
  if (isAir(target)) return s.vsAir ?? 0;
  if (isNaval(target)) return s.vsNaval ?? 0;
  return s.vsGround ?? 100;
};
export const moveCap = (st, u) => {
  const n = stats(u).move + (isNaval(u) && hasEffect(st, "rough-seas") ? EFFECTS["rough-seas"].shipMove : 0);
  return Math.max(1, n);
};
export const sortieCost = (st) => RULES.sortieAp + (hasEffect(st, "storm") ? EFFECTS.storm.sortieExtra : 0);
export const fireRange = (st, s) => {
  let [lo, hi] = s.range;
  if (hasEffect(st, "fog") && !s.indirect) hi = Math.min(hi, EFFECTS.fog.directRangeCap);
  return [lo, hi];
};
export const structureHp = (st, type) => {
  const hp = STRUCTURES[type].hp;
  return hasEffect(st, "fortified") ? Math.floor(hp * EFFECTS.fortified.structureHp) : hp;
};
export const researchCostOf = (st, tech) => {
  const base = RULES.researchCost[tech];
  return hasEffect(st, "tech-race") ? Math.floor(base * EFFECTS["tech-race"].researchCost) : base;
};
export const isCoastal = (st, h) => {
  if (!inBounds(st, h)) return false;
  const id = terrainId(st, h);
  if (id === "water" || TERRAIN[id].cost === null) return false;
  return DIRS.some((d) => inBounds(st, add(h, d)) && terrainId(st, add(h, d)) === "water");
};
export const levelOf = (st, h) => TERRAIN[terrainId(st, h)].level ?? 0;
// Firing height: plateaus count two steps, hills one, so hills fire down on flat ground but not on plateaus.
export const heightOf = (st, h) => { const t = TERRAIN[terrainId(st, h)]; return (t.level ?? 0) * 2 + (t.vantage ?? 0); };
export const linkBetween = (st, a, b) =>
  (st.links ?? []).find((l) => (eq(l.a, a) && eq(l.b, b)) || (eq(l.a, b) && eq(l.b, a))) ?? null;
const jumpPartners = (st, h) => (st.links ?? []).filter((l) => l.kind === "jump" && (eq(l.a, h) || eq(l.b, h)))
  .map((l) => (eq(l.a, h) ? l.b : l.a));
const clone = (o) => structuredClone(o);

// Decision 2: Command Base hexes are structures. No unit may enter one.
// Ships pay 1 AP on water only; land units treat water as impassable (fords stay land).
const entryCost = (st, h, u) => {
  if (baseAt(st, h) || structureAt(st, h)) return null;
  const id = terrainId(st, h);
  if (u && UNITS[u.type]?.naval) return id === "water" ? 1 : null;
  if (id === "water") return null;
  return TERRAIN[id].cost;
};
// Decision 3: hexes adjacent to an enemy infantry/tank are in its zone of control.
// A cliff edge cuts ZOC unless a stairs/ladder link joins the two hexes.
export const inEnemyZOC = (st, h, owner) =>
  DIRS.some((d) => {
    const n = add(h, d), u = unitAt(st, n);
    return u && u.owner !== owner && stats(u).zoc && (levelOf(st, n) === levelOf(st, h) || !!linkBetween(st, h, n));
  });

// One MOVE step, ignoring occupancy and ZOC: an adjacent walk (a level change needs a stairs/ladder link)
// or a jump-pad hop, which costs a flat RULES.jumpAp and must be the last step.
function stepRule(st, u, from, to) {
  if (stats(u).air) {
    if (dist(from, to) !== 1) return { error: "NOT_ADJACENT" };
    if (!inBounds(st, to)) return { error: "OUT_OF_BOUNDS" };
    if (baseAt(st, to) || structureAt(st, to)) return { error: "IMPASSABLE" };
    return { cost: 1 };
  }
  if (stats(u).naval) {
    if (dist(from, to) !== 1) return { error: "NOT_ADJACENT" };
    if (!inBounds(st, to)) return { error: "OUT_OF_BOUNDS" };
    if (entryCost(st, to, u) === null) return { error: "IMPASSABLE" };
    return { cost: 1 };
  }
  const link = linkBetween(st, from, to);
  const hop = link?.kind === "jump";
  if (!hop && dist(from, to) !== 1) return { error: "NOT_ADJACENT" };
  if (!inBounds(st, to)) return { error: "OUT_OF_BOUNDS" };
  const c = entryCost(st, to, u); if (c === null) return { error: "IMPASSABLE" };
  if (link && !LINKS[link.kind].types.includes(u.type)) return { error: "LINK_UNIT", link: link.kind };
  if (hop) return { cost: RULES.jumpAp, end: true };
  if (levelOf(st, from) !== levelOf(st, to)) return link ? { cost: c + LINKS[link.kind].extra } : { error: "CLIFF" };
  return { cost: c };
}

// ---------------------------------------------------------------- formations
export function wedgeAt(st, lead) {
  if (!lead || lead.fresh) return null;
  const kind = lead.type === "tank" ? "armored" : lead.type === "infantry" ? "fireteam" : null;
  if (!kind) return null;
  const wings = [2, 4].map((o) => unitAt(st, neighbor(lead, lead.facing + o)));
  const ok = (w) => w && w.owner === lead.owner && w.facing === lead.facing && !w.fresh &&
    (kind === "armored" ? ["tank", "infantry"].includes(w.type) : w.type === "infantry");
  return wings.every(ok) ? { kind, lead: lead.id, wings: wings.map((w) => w.id) } : null;
}

function selection(st, ids) {
  if (new Set(ids).size !== ids.length) return { error: "DUPLICATE_UNIT" };
  if (ids.length < RULES.minGroup || ids.length > RULES.maxGroup) return { error: "GROUP_SIZE" };
  const units = ids.map((id) => byId(st, id));
  if (units.some((u) => !u)) return { error: "UNKNOWN_UNIT" };
  if (units.some((u) => u.owner !== st.turn.player)) return { error: "NOT_YOUR_UNIT" };
  return { units };
}
const capCheck = (st, u, hexes) => (u.moved ?? 0) + hexes > moveCap(st, u) ? { error: "MOVE_CAP", unit: u.id } : null;

// ---------------------------------------------------------------- MOVE (single unit)
export function planMove(st, id, path) {
  const u = byId(st, id);
  if (!u) return { error: "UNKNOWN_UNIT" };
  if (u.owner !== st.turn.player) return { error: "NOT_YOUR_UNIT" };
  if (!path.length) return { error: "EMPTY_PATH" };
  const cap = capCheck(st, u, path.length); if (cap) return cap;
  let prev = u, cost = 0;
  for (let i = 0; i < path.length; i++) {
    const h = path[i];
    const step = stepRule(st, u, prev, h); if (step.error) return { ...step, at: h };
    if (unitAt(st, h)) return { error: "BLOCKED", at: h };
    if (step.end && i < path.length - 1) return { error: "JUMP_ENDS_MOVE", at: h };
    cost += step.cost;
    if (i < path.length - 1 && !stats(u).air && inEnemyZOC(st, h, u.owner)) return { error: "ZOC_STOP", at: h };
    prev = h;
  }
  if (stats(u).sortie) cost = sortieCost(st); // jets fly any distance for one flat sortie cost
  return { ok: true, cost, moves: [{ id, to: path.at(-1), hexes: path.length }] };
}

/** Legal destinations for a single MOVE, with a cheapest path to each hex. */
export function reachableHexes(st, id) {
  const u = byId(st, id);
  if (!u || u.owner !== st.turn.player) return [];
  const remain = moveCap(st, u) - (u.moved ?? 0);
  const flat = stats(u).sortie ? sortieCost(st) : null, air = !!stats(u).air;
  if (remain <= 0 || st.turn.ap <= 0 || (flat !== null && flat > st.turn.ap)) return [];
  const best = new Map();
  const queue = [{ q: u.q, r: u.r, path: [], cost: 0, hexes: 0 }];
  while (queue.length) {
    const cur = queue.shift();
    if (cur.path.length && !air && inEnemyZOC(st, cur, u.owner)) continue;
    if (cur.end || cur.hexes >= remain) continue;
    for (const h of [...DIRS.map((d) => add(cur, d)), ...jumpPartners(st, cur)]) {
      const step = stepRule(st, u, cur, h);
      if (step.error || unitAt(st, h)) continue;
      const hexes = cur.hexes + 1;
      const cost = flat ?? cur.cost + step.cost;
      if (hexes > remain || cost > st.turn.ap) continue;
      const k = key(h);
      const prev = best.get(k);
      if (prev && (prev.cost < cost || (prev.cost === cost && prev.hexes <= hexes))) continue;
      const rec = { q: h.q, r: h.r, path: [...cur.path, { q: h.q, r: h.r }], cost, hexes, ...(step.end ? { end: true } : {}) };
      best.set(k, rec);
      queue.push(rec);
    }
  }
  return [...best.values()];
}

/** Legal attack tiles for the HUD telegraph. */
export function attackTargets(st, attackerId) {
  const atk = byId(st, attackerId);
  if (!atk) return [];
  const out = [];
  for (const e of st.units) {
    if (e.owner === atk.owner) continue;
    const plan = planAttack(st, attackerId, { unitId: e.id });
    if (plan.ok) out.push({ q: e.q, r: e.r, kind: "unit", targetId: e.id, plan });
  }
  for (const b of st.bases ?? []) {
    if (b.owner === atk.owner || (b.hp ?? 0) <= 0) continue;
    const plan = planAttack(st, attackerId, { base: b.owner });
    if (plan.ok) out.push({ q: b.q, r: b.r, kind: "base", targetBase: b.owner, plan });
  }
  for (const s of st.structures ?? []) {
    if (s.owner === atk.owner) continue;
    const plan = planAttack(st, attackerId, { structureId: s.id });
    if (plan.ok) out.push({ q: s.q, r: s.r, kind: "structure", targetId: s.id, plan });
  }
  return out;
}

// ---------------------------------------------------------------- GROUP_MOVE (translation)
export function planGroupMove(st, ids, dir) {
  const sel = selection(st, ids); if (sel.error) return sel;
  const set = new Set(ids); let maxCost = 0;
  const moves = sel.units.map((u) => ({ id: u.id, to: neighbor(u, dir), hexes: 1 }));
  for (const [i, m] of moves.entries()) {
    const cap = capCheck(st, sel.units[i], 1); if (cap) return cap;
    if (!inBounds(st, m.to)) return { error: "OUT_OF_BOUNDS", at: m.to };
    const c = entryCost(st, m.to, sel.units[i]); if (c === null) return { error: "IMPASSABLE", at: m.to };
    if (levelOf(st, m.to) !== levelOf(st, sel.units[i])) return { error: "LEVEL_CHANGE", at: m.to }; // links are single-file
    const occ = unitAt(st, m.to); if (occ && !set.has(occ.id)) return { error: "BLOCKED", at: m.to };
    maxCost = Math.max(maxCost, c);
  }
  let cost = maxCost + 1, discount = null;
  if (ids.length === 3 && maxCost === 1 && !st.turn.coordinatedAdvanceUsed) {
    const lead = sel.units.find((u) => wedgeAt(st, u)), w = lead && wedgeAt(st, lead);
    if (w && dir === lead.facing && [w.lead, ...w.wings].every((x) => set.has(x))) { cost = 1; discount = "COORDINATED_ADVANCE"; }
  }
  return { ok: true, cost, discount, moves };
}

// Multi-step order: validated end-to-end on a scratch copy; atomic. Optional final shared facing.
export function planGroupOrder(st, ids, steps, facing) {
  if (!steps.length) return { error: "EMPTY_PATH" };
  const sim = clone(st); let cost = 0; const plans = [];
  for (let i = 0; i < steps.length; i++) {
    const p = planGroupMove(sim, ids, steps[i]); if (p.error) return { ...p, step: i };
    if (cost + p.cost > st.turn.ap) return { error: "INSUFFICIENT_AP", step: i };
    applyMoves(sim, p); cost += p.cost; plans.push(p);
    if (i < steps.length - 1 && ids.some((id) => inEnemyZOC(sim, byId(sim, id), st.turn.player))) return { error: "ZOC_STOP", step: i };
  }
  return { ok: true, cost, plans, facing };
}

// ---------------------------------------------------------------- WHEEL
export function planWheel(st, ids, pivotId, rot) {
  const sel = selection(st, ids); if (sel.error) return sel;
  if (!ids.includes(pivotId)) return { error: "PIVOT_NOT_IN_GROUP" };
  const pivot = byId(st, pivotId), set = new Set(ids);
  let maxCost = 1, broken = false; const moves = [];
  for (const u of sel.units) {
    if (dist(pivot, u) > RULES.maxWheelRadius) return { error: "WHEEL_RADIUS", unit: u.id };
    const path = arcPath(pivot, u, rot);
    const cap = capCheck(st, u, path.length); if (cap) return cap;
    for (const h of path) {
      if (!inBounds(st, h)) return { error: "OUT_OF_BOUNDS", at: h, unit: u.id };
      const c = entryCost(st, h, u); if (c === null) return { error: "IMPASSABLE", at: h, unit: u.id };
      if (levelOf(st, h) !== levelOf(st, u)) return { error: "LEVEL_CHANGE", at: h, unit: u.id };
      const occ = unitAt(st, h); if (occ && !set.has(occ.id)) return { error: "BLOCKED", at: h, unit: u.id };
      if (terrainAt(st, h).control === "broken") {
        if (stats(u).cls === "armor") return { error: "ARMOR_CANNOT_WHEEL_IN_BROKEN", at: h, unit: u.id };
        broken = true;
      }
      maxCost = Math.max(maxCost, c);
    }
    moves.push({ id: u.id, to: path.length ? path.at(-1) : { q: u.q, r: u.r }, path, hexes: path.length, facing: turnFacing(u.facing, rot) });
  }
  return { ok: true, cost: maxCost + 1 + (broken ? 1 : 0), moves };
}

// ---------------------------------------------------------------- FACE / GROUP_FACE
export function planFace(st, ids, facing) {
  if (!Number.isInteger(facing) || facing < 0 || facing > 5) return { error: "BAD_FACING" };
  const units = ids.map((id) => byId(st, id));
  if (units.some((u) => !u)) return { error: "UNKNOWN_UNIT" };
  if (units.some((u) => u.owner !== st.turn.player)) return { error: "NOT_YOUR_UNIT" };
  if (ids.length > 1) { const s = selection(st, ids); if (s.error) return s; }
  return { ok: true, cost: 1, moves: units.map((u) => ({ id: u.id, to: { q: u.q, r: u.r }, hexes: 0, facing })) };
}

function applyMoves(st, plan, facing) {
  for (const m of plan.moves) {
    const u = byId(st, m.id); u.q = m.to.q; u.r = m.to.r; u.moved = (u.moved ?? 0) + m.hexes;
    if (m.facing !== undefined) u.facing = m.facing;
    if (facing !== undefined) u.facing = facing;
  }
  if (plan.discount === "COORDINATED_ADVANCE") st.turn.coordinatedAdvanceUsed = true;
}
export function apply(st, plan, facing) {
  if (!plan.ok) throw new Error(plan.error);
  if (plan.cost > st.turn.ap) throw new Error("INSUFFICIENT_AP");
  if (plan.plans) plan.plans.forEach((p, i) => applyMoves(st, p, i === plan.plans.length - 1 ? plan.facing : undefined));
  else applyMoves(st, plan, facing);
  st.turn.ap -= plan.cost;
  return st;
}

// ---------------------------------------------------------------- COMBAT
const CATEGORY = ["front", "front", "flank", "rear", "flank", "front"]; // index = (dir - facing) mod 6

// Direction from defender toward attacker, snapped to the nearest hex direction; ties favour the defender.
export function attackAngle(defender, attacker) {
  const d = sub(attacker, defender);
  const exact = DIRS.findIndex((v) => eq(v, d));
  const cat = (dir) => CATEGORY[(dir - defender.facing + 6) % 6];
  if (exact >= 0) return cat(exact);
  const a = pixel(attacker), b = pixel(defender);
  const ang = (Math.atan2(a.y - b.y, a.x - b.x) * 180) / Math.PI;
  const diffs = DIRS.map((_, i) => { const x = Math.abs(((ang - i * 60) % 360 + 540) % 360 - 180); return x; });
  const min = Math.min(...diffs);
  const cands = diffs.map((x, i) => [x, i]).filter(([x]) => x - min < 1e-6).map(([, i]) => cat(i));
  return cands.sort((p, q) => RULES.angle[p] - RULES.angle[q])[0];
}

export function hasLOS(st, a, b) { return lineBetween(a, b).every((h) => !terrainAt(st, h).blocksLOS); }

function canHit(st, u, target) {
  const s = stats(u), d = dist(u, target);
  if (s.atk <= 0 || vsAgainst(s, target) <= 0) return false;
  if (s.indirect && (u.moved ?? 0) > 0) return false;
  const [lo, hi] = fireRange(st, s);
  if (d < lo || d > hi) return false;
  return s.indirect || s.air || isAir(target) || hasLOS(st, u, target);
}

// Bulwark Protocol: GVS tanks adjacent to friendly infrastructure (own base) or a friendly tank.
function bulwark(st, u) {
  if (st.players?.[u.owner]?.faction !== "GVS" || u.type !== "tank") return 0;
  const ok = DIRS.some((d) => {
    const h = add(u, d), b = baseAt(st, h), f = unitAt(st, h);
    return (b && b.owner === u.owner) || (f && f.owner === u.owner && f.type === "tank");
  });
  return ok ? RULES.bulwark : 0;
}

// target: { unitId }, { base: owner } or { structureId }
export function planAttack(st, attackerId, target) {
  const atk = byId(st, attackerId);
  if (!atk) return { error: "UNKNOWN_UNIT" };
  if (atk.owner !== st.turn.player) return { error: "NOT_YOUR_UNIT" };
  if (atk.fresh) return { error: "FRESH_UNIT" };
  if (atk.attacked) return { error: "ALREADY_ATTACKED" };
  if (st.turn.ap < RULES.attackCost) return { error: "INSUFFICIENT_AP" };
  const s = stats(atk);
  if (s.indirect && (atk.moved ?? 0) > 0) return { error: "MOVED_INDIRECT" };
  const isBase = target.base !== undefined, isStructure = target.structureId !== undefined;
  const def = isBase ? (st.bases ?? []).find((b) => b.owner === target.base)
    : isStructure ? structureById(st, target.structureId) : byId(st, target.unitId);
  if (!def) return { error: "UNKNOWN_TARGET" };
  if (def.owner === atk.owner) return { error: "FRIENDLY_TARGET" };
  if (s.atk <= 0) return { error: "NO_WEAPON" };
  const defAir = !isBase && !isStructure && isAir(def);
  const defNaval = !isBase && !isStructure && isNaval(def);
  const vs = vsAgainst(s, def);
  if (vs <= 0) return { error: "CANNOT_TARGET" };
  const d = dist(atk, def);
  const [lo, hi] = fireRange(st, s);
  if (d < lo || d > hi) return { error: "OUT_OF_RANGE" };
  if (!s.indirect && !s.air && !defAir && !hasLOS(st, atk, def)) return { error: "NO_LOS" };

  const mods = []; let bonus = 0, cover = 0, reduction = 0, armor = 0, angle = "front";
  // 0a. firing down from a plateau or hills (ground on ground only)
  if (!s.air && !defAir && !defNaval && heightOf(st, atk) > heightOf(st, def)) { bonus += RULES.highGround; mods.push(["high_ground", RULES.highGround]); }
  // 0b. technology edge: each research tier of difference is worth RULES.techEdge, either way
  const edge = RULES.techEdge * (techOf(st, atk.owner) - techOf(st, def.owner));
  if (edge) { bonus += edge; mods.push(["tech_edge", edge]); }
  if (vs !== 100) mods.push([defAir ? "vs_air" : defNaval ? "vs_naval" : "vs_ground", vs - 100]);
  if (hasEffect(st, "storm") && s.air) { bonus += EFFECTS.storm.airAtk; mods.push(["storm", EFFECTS.storm.airAtk]); }
  if (hasEffect(st, "rough-seas") && s.naval) { bonus += EFFECTS["rough-seas"].navalAtk; mods.push(["rough-seas", EFFECTS["rough-seas"].navalAtk]); }
  if (isBase) {
    cover = RULES.baseCover; mods.push(["base_cover", -cover]);
  } else if (isStructure) {
    armor = STRUCTURES[def.type].armor; if (armor) mods.push(["flat_armor", -armor]);
  } else if (defAir) {
    armor = stats(def).armor; if (armor) mods.push(["flat_armor", -armor]); // airborne: no cover, facing or formations
  } else {
    const defTerrain = terrainId(st, def);
    // 1-2. offensive percentages (additive)
    if (!s.indirect) {
      angle = attackAngle(def, atk);
      if (RULES.angle[angle]) { bonus += RULES.angle[angle]; mods.push([angle, RULES.angle[angle]]); }
      if (angle !== "front") {
        const pin = [0, 1, 5].some((o) => {
          const p = unitAt(st, neighbor(def, def.facing + o));
          return p && p.id !== atk.id && p.owner === atk.owner && !p.fresh && !stats(p).indirect && canHit(st, p, def);
        });
        if (pin) { bonus += RULES.crossfire; mods.push(["crossfire", RULES.crossfire]); }
      }
      const w = wedgeAt(st, atk);
      if (w && d === 1 && eq(neighbor(atk, atk.facing), def)) {
        // Decision 6: Fire Team Spearhead ignores the Ruins restriction; Armored Spearhead does not.
        const blocked = defTerrain === "ruins" && w.kind === "armored";
        if (!blocked) { bonus += RULES.spearhead[w.kind]; mods.push([`spearhead_${w.kind}`, RULES.spearhead[w.kind]]); }
        else mods.push(["spearhead_blocked_by_ruins", 0]);
      }
    }
    // 3. terrain cover
    cover = TERRAIN[defTerrain].cover; if (cover) mods.push(["terrain_cover", -cover]);
    // 4. strongest single formation / faction reduction
    let braced = 0; const dw = wedgeAt(st, def);
    if (dw?.kind === "armored" && !s.indirect && angle === "front" && defTerrain !== "ruins") braced = RULES.bracedFront;
    const bul = bulwark(st, def);
    reduction = Math.max(braced, bul);
    if (reduction) mods.push([bul >= braced ? "bulwark" : "braced_front", -reduction]);
    // 5. flat armor
    armor = stats(def).armor; if (armor) mods.push(["flat_armor", -armor]);
  }
  // 6. single floor, exact integer arithmetic; minimum 1
  const num = s.atk * (100 + bonus) * (100 - cover) * (100 - reduction) * vs - armor * 1e8;
  const damage = Math.max(1, Math.floor(num / 1e8));
  return { ok: true, cost: RULES.attackCost, attacker: atk.id, target, angle: isBase || isStructure || defAir ? null : angle, bonus, cover, reduction, armor, damage, mods };
}

export function applyAttack(st, plan) {
  if (!plan.ok) throw new Error(plan.error);
  if (plan.cost > st.turn.ap) throw new Error("INSUFFICIENT_AP");
  const atk = byId(st, plan.attacker); atk.attacked = true; st.turn.ap -= plan.cost;
  const result = { damage: plan.damage, destroyed: false, captured: null };
  if (plan.target.base !== undefined) {
    const b = st.bases.find((x) => x.owner === plan.target.base);
    if (b.hp !== undefined) { b.hp = Math.max(0, b.hp - plan.damage); result.destroyed = b.hp === 0; }
    return result;
  }
  if (plan.target.structureId !== undefined) {
    const s = structureById(st, plan.target.structureId);
    s.hp -= plan.damage;
    if (s.hp <= 0) { st.structures = st.structures.filter((x) => x.id !== s.id); result.destroyed = true; }
    return result;
  }
  const def = byId(st, plan.target.unitId);
  def.hp = (def.hp ?? stats(def).hp) - plan.damage;
  if (def.hp <= 0) {
    st.units = st.units.filter((u) => u.id !== def.id);
    (st.reserves ??= {}); (st.reserves[atk.owner] ??= []).push({ id: def.id, type: def.type, from: def.owner });
    if (st.players?.[atk.owner]?.faction === "BOE") st.players[atk.owner].credits += RULES.scavengerBonus;
    result.destroyed = true; result.captured = def.id;
  }
  return result;
}

// ---------------------------------------------------------------- turn flow (Decision 4: ore control)
export function endTurn(st) {
  const p = st.turn.player;
  for (const u of st.units) if (u.owner === p && u.type === "infantry" && terrainId(st, u) === "ore") (st.oreOwner ??= {})[key(u)] = p;
  const next = p === 1 ? 2 : 1;
  // Defense phase: the waiting side's structures fire at enemies that ended this turn in range.
  const volleys = structureVolleys(st, next);
  for (const u of st.units) { u.moved = 0; u.attacked = false; if (u.owner === next) u.fresh = false; }
  const prevRound = st.turn.round ?? 1;
  const number = (st.turn.number ?? 1) + 1;
  const round = next === 1 ? prevRound + 1 : prevRound;
  st.turn = { player: next, ap: rule(st, "apPerTurn"), coordinatedAdvanceUsed: false, number, round };
  // Economy phase for the incoming player
  const income = incomeBreakdown(st, next).total;
  if (st.players?.[next]) st.players[next].credits += income;
  // After the last scheduled round, the first player gets a final reply so the second mover
  // does not automatically take the last word (P2 timeout bias).
  if (round > rule(st, "maxTurns")) {
    if (st.finalReply) st.timeExpired = true;
    else if (next === (st.firstPlayer ?? 1)) st.finalReply = true;
    else st.timeExpired = true;
  }
  return { player: next, income, volleys, timeExpired: !!st.timeExpired, winner: winner(st) };
}

// ---------------------------------------------------------------- structures (attack-and-defend layer)
/** Damage a structure would deal to a unit, or null if it cannot shoot it. */
export function planStructureShot(st, s, target) {
  const t = STRUCTURES[s.type];
  if (!t.atk) return null;
  const vs = vsAgainst(t, target);
  if (vs <= 0 || target.owner === s.owner) return null;
  const d = dist(s, target);
  const [lo, hi] = fireRange(st, t);
  if (d < lo || d > hi) return null;
  if (!isAir(target) && !hasLOS(st, s, target)) return null;
  const bonus = RULES.techEdge * (techOf(st, s.owner) - techOf(st, target.owner));
  const cover = isAir(target) ? 0 : TERRAIN[terrainId(st, target)].cover;
  const armor = stats(target).armor;
  const num = t.atk * (100 + bonus) * (100 - cover) * 100 * vs - armor * 1e8;
  return { damage: Math.max(1, Math.floor(num / 1e8)), bonus, cover, armor };
}

function structureVolleys(st, owner) {
  const out = [];
  for (const s of st.structures ?? []) {
    if (s.owner !== owner) continue;
    let pick = null;
    for (const u of st.units) {
      const shot = planStructureShot(st, s, u);
      if (!shot) continue;
      const left = (u.hp ?? stats(u).hp) - shot.damage;
      if (!pick || left < pick.left || (left === pick.left && dist(s, u) < dist(s, pick.u))) pick = { u, shot, left };
    }
    if (!pick) continue;
    const { u, shot, left } = pick;
    out.push({ from: s.id, type: s.type, fromHex: { q: s.q, r: s.r }, to: { q: u.q, r: u.r }, target: u.id, damage: shot.damage, destroyed: left <= 0 });
    u.hp = left;
    if (left <= 0) st.units = st.units.filter((x) => x.id !== u.id);
  }
  return out;
}

/** Hexes where `owner` may build: near its base or an ore vein it holds; open, empty, not ore.
 *  `type` restricts docks and coastal guns to coastal land, and sea structures to sea-allowed maps. */
export function buildHexes(st, owner, type) {
  const base = (st.bases ?? []).find((b) => b.owner === owner && (b.hp ?? 1) > 0);
  if (!base) return [];
  const t = type ? STRUCTURES[type] : null;
  if (t?.coastal && !domainAllowed(st, "sea")) return [];
  const held = Object.entries(st.oreOwner ?? {}).filter(([, o]) => o === owner)
    .map(([k]) => { const [q, r] = k.split(",").map(Number); return { q, r }; });
  return boardCells(st).filter((h) =>
    (dist(base, h) <= RULES.buildRadius || held.some((o) => dist(o, h) <= 2)) &&
    entryCost(st, h) !== null && !unitAt(st, h) && terrainId(st, h) !== "ore" &&
    (!t?.coastal || isCoastal(st, h)));
}

export function planBuild(st, type, at) {
  const t = STRUCTURES[type]; if (!t) return { error: "UNKNOWN_TYPE" };
  const p = st.turn.player;
  if (t.coastal && !domainAllowed(st, "sea")) return { error: "DOMAIN_LOCKED" };
  if (t.tech > techOf(st, p)) return { error: "NEEDS_TECH", tech: t.tech };
  if ((st.players?.[p]?.credits ?? 0) < t.cost) return { error: "CANNOT_AFFORD" };
  if (st.turn.ap < t.ap) return { error: "INSUFFICIENT_AP" };
  if (!at || !buildHexes(st, p, type).some((h) => eq(h, at))) return { error: "BAD_BUILD_HEX", at };
  return { ok: true, cost: t.ap, credits: t.cost, type, to: { q: at.q, r: at.r } };
}

export function applyBuild(st, plan) {
  if (!plan.ok) throw new Error(plan.error);
  const p = st.turn.player;
  if (plan.cost > st.turn.ap) throw new Error("INSUFFICIENT_AP");
  if ((st.players?.[p]?.credits ?? 0) < plan.credits) throw new Error("CANNOT_AFFORD");
  const s = { id: mintId(st, plan.type, p), type: plan.type, owner: p, q: plan.to.q, r: plan.to.r, hp: structureHp(st, plan.type) };
  (st.structures ??= []).push(s);
  st.players[p].credits -= plan.credits;
  st.turn.ap -= plan.cost;
  return s;
}

// ---------------------------------------------------------------- research
export function planResearch(st) {
  const p = st.turn.player, next = techOf(st, p) + 1;
  if (next > RULES.maxTech) return { error: "MAX_TECH" };
  const credits = researchCostOf(st, next);
  if ((st.players?.[p]?.credits ?? 0) < credits) return { error: "CANNOT_AFFORD" };
  if (st.turn.ap < RULES.researchAp) return { error: "INSUFFICIENT_AP" };
  return { ok: true, cost: RULES.researchAp, credits, tech: next };
}

export function applyResearch(st, plan) {
  if (!plan.ok) throw new Error(plan.error);
  const p = st.turn.player;
  if (plan.cost > st.turn.ap) throw new Error("INSUFFICIENT_AP");
  st.players[p].credits -= plan.credits;
  st.players[p].tech = plan.tech;
  st.turn.ap -= plan.cost;
  return plan.tech;
}

// ---------------------------------------------------------------- production / drop / victory
/** Water hexes adjacent to `owner`'s docks — where ships are launched. */
export function shipDropHexes(st, owner) {
  const spots = [], seen = new Set();
  for (const s of st.structures ?? []) {
    if (s.owner !== owner || s.type !== "dock") continue;
    for (const d of DIRS) {
      const h = add(s, d);
      if (!inBounds(st, h) || terrainId(st, h) !== "water") continue;
      if (unitAt(st, h) || baseAt(st, h) || structureAt(st, h)) continue;
      const k = key(h);
      if (seen.has(k)) continue;
      seen.add(k);
      spots.push(h);
    }
  }
  return spots;
}

export function dropHexes(st, owner, type) {
  if (type && UNITS[type]?.naval) return shipDropHexes(st, owner);
  const base = (st.bases ?? []).find((b) => b.owner === owner && (b.hp ?? 1) > 0);
  if (!base) return [];
  return DIRS.map((d) => add(base, d)).filter((h) =>
    inBounds(st, h) && !unitAt(st, h) && !baseAt(st, h) && entryCost(st, h) !== null);
}

export function mintId(st, type, owner) {
  st.seq = (st.seq ?? 0) + 1;
  const tag = st.players?.[owner]?.faction === "BOE" ? "boe" : "gvs";
  return `${tag}-${type}-${st.seq}`;
}

// Optional `to` picks one of dropHexes(); omitted, the first free hex is used.
function pickDrop(st, p, to, type) {
  const spots = dropHexes(st, p, type);
  if (!spots.length) return { error: "NO_DROP_HEX" };
  if (!to) return { to: spots[0] };
  const hit = spots.find((h) => eq(h, to));
  return hit ? { to: hit } : { error: "BAD_DROP_HEX", at: to };
}

export function planProduce(st, type, to) {
  if (!UNITS[type]) return { error: "UNKNOWN_TYPE" };
  const p = st.turn.player;
  if (!domainAllowed(st, UNIT_DOMAIN[type] ?? "land")) return { error: "DOMAIN_LOCKED" };
  if ((UNIT_TECH[type] ?? 1) > techOf(st, p)) return { error: "NEEDS_TECH", tech: UNIT_TECH[type] };
  const credits = st.players?.[p]?.credits ?? 0;
  const costAp = UNIT_AP_COST[type], costCr = UNIT_COSTS[type];
  if (credits < costCr) return { error: "CANNOT_AFFORD" };
  if (st.turn.ap < costAp) return { error: "INSUFFICIENT_AP" };
  const spot = pickDrop(st, p, to, type); if (spot.error) return spot;
  return { ok: true, cost: costAp, credits: costCr, type, to: spot.to, facing: p === 1 ? 1 : 4 };
}

export function applyProduce(st, plan) {
  if (!plan.ok) throw new Error(plan.error);
  if (plan.cost > st.turn.ap) throw new Error("INSUFFICIENT_AP");
  const p = st.turn.player;
  if ((st.players?.[p]?.credits ?? 0) < plan.credits) throw new Error("CANNOT_AFFORD");
  const occ = unitAt(st, plan.to);
  if (occ || !inBounds(st, plan.to) || entryCost(st, plan.to, { type: plan.type }) === null) throw new Error("NO_DROP_HEX");
  st.units.push({
    id: mintId(st, plan.type, p), type: plan.type, owner: p,
    q: plan.to.q, r: plan.to.r, facing: plan.facing,
    hp: UNITS[plan.type].hp, moved: 0, attacked: false, fresh: true,
  });
  st.players[p].credits -= plan.credits;
  st.turn.ap -= plan.cost;
  return st.units.at(-1);
}

export function planDrop(st, reserveIndex, to) {
  const p = st.turn.player;
  const bag = st.reserves?.[p] ?? [];
  const unit = bag[reserveIndex];
  if (!unit) return { error: "EMPTY_RESERVE" };
  if (st.turn.ap < RULES.dropAp) return { error: "INSUFFICIENT_AP" };
  const spot = pickDrop(st, p, to, unit.type); if (spot.error) return spot;
  return { ok: true, cost: RULES.dropAp, reserveIndex, type: unit.type, to: spot.to, facing: p === 1 ? 1 : 4 };
}

export function applyDrop(st, plan) {
  if (!plan.ok) throw new Error(plan.error);
  if (plan.cost > st.turn.ap) throw new Error("INSUFFICIENT_AP");
  const p = st.turn.player;
  const bag = st.reserves?.[p] ?? [];
  const unit = bag[plan.reserveIndex];
  if (!unit) throw new Error("EMPTY_RESERVE");
  const occ = unitAt(st, plan.to);
  if (occ || !inBounds(st, plan.to) || entryCost(st, plan.to, { type: plan.type }) === null) throw new Error("NO_DROP_HEX");
  bag.splice(plan.reserveIndex, 1);
  st.units.push({
    id: unit.id, type: unit.type, owner: p,
    q: plan.to.q, r: plan.to.r, facing: plan.facing,
    hp: UNITS[unit.type].hp, moved: 0, attacked: false, fresh: true,
  });
  st.turn.ap -= plan.cost;
  return st.units.at(-1);
}

export function scoreOf(st, p) {
  const enemy = (st.bases ?? []).find((b) => b.owner !== p);
  const dmg = enemy ? (enemy.hpMax ?? RULES.baseHp) - (enemy.hp ?? 0) : 0;
  const ore = Object.values(st.oreOwner ?? {}).filter((o) => o === p).length;
  const cred = st.players?.[p]?.credits ?? 0;
  const hp = st.units.filter((u) => u.owner === p).reduce((n, u) => n + (u.hp ?? 0), 0);
  return dmg * 2 + ore * 100 + Math.floor(cred / 10) + hp;
}

export function winner(st) {
  const bases = st.bases ?? [];
  const down = bases.filter((b) => (b.hp ?? 0) <= 0);
  if (down.length === 1) return down[0].owner === 1 ? 2 : 1;
  if (down.length >= 2 || st.timeExpired) {
    const a = scoreOf(st, 1), b = scoreOf(st, 2);
    if (a === b) return 0;
    return a > b ? 1 : 2;
  }
  return null;
}

export function threatCells(st, defenderOwner) {
  const cells = [];
  for (const u of st.units) {
    if (u.owner === defenderOwner || u.fresh) continue;
    const s = stats(u);
    if (s.atk <= 0) continue;
    for (const t of st.units) {
      if (t.owner === u.owner) continue;
      if (canHit(st, u, t)) cells.push({ q: t.q, r: t.r, from: u.id, kind: "unit", targetId: t.id });
    }
    for (const b of st.bases ?? []) {
      if (b.owner === u.owner || (b.hp ?? 0) <= 0) continue;
      const d = dist(u, b);
      const [lo, hi] = fireRange(st, s);
      if (d >= lo && d <= hi && (s.indirect || s.air || hasLOS(st, u, b))) {
        cells.push({ q: b.q, r: b.r, from: u.id, kind: "base", targetBase: b.owner });
      }
    }
    for (const t of st.structures ?? []) {
      if (t.owner === u.owner || vsAgainst(s, t) <= 0) continue;
      const d = dist(u, t);
      const [lo, hi] = fireRange(st, s);
      if (d >= lo && d <= hi && (s.indirect || s.air || hasLOS(st, u, t))) {
        cells.push({ q: t.q, r: t.r, from: u.id, kind: "structure", targetId: t.id });
      }
    }
  }
  for (const s of st.structures ?? []) {
    if (s.owner === defenderOwner) continue;
    for (const t of st.units) {
      if (t.owner === defenderOwner && planStructureShot(st, s, t)) cells.push({ q: t.q, r: t.r, from: s.id, kind: "unit", targetId: t.id });
    }
  }
  return cells;
}

// Phytane income the player collects at the start of their next turn, line by line (same math as endTurn).
export function incomeBreakdown(st, player) {
  const ore = Object.values(st.oreOwner ?? {}).filter((o) => o === player).length;
  const harvesters = st.units.filter((u) => u.owner === player && u.type === "harvester" && terrainId(st, u) === "ore" &&
    (st.oreOwner?.[key(u)] ?? player) === player).length;
  const vein = hasEffect(st, "rich-veins") ? EFFECTS["rich-veins"].veinIncome : 1;
  const oreEach = Math.floor(RULES.oreIncome * vein), harvEach = Math.floor(RULES.harvesterIncome * vein);
  const lines = [
    { id: "base", count: 1, each: RULES.baseIncome, total: RULES.baseIncome },
    { id: "ore", count: ore, each: oreEach, total: ore * oreEach, ...(vein !== 1 ? { effect: "rich-veins" } : {}) },
    { id: "harvester", count: harvesters, each: harvEach, total: harvesters * harvEach, ...(vein !== 1 ? { effect: "rich-veins" } : {}) },
  ];
  return { lines, total: lines.reduce((n, l) => n + l.total, 0), stock: st.players?.[player]?.credits ?? 0 };
}

export function incomePreview(st, player) {
  return incomeBreakdown(st, player).total;
}

export function influenceAt(st, h) {
  const k = key(h);
  if (st.oreOwner?.[k]) return st.oreOwner[k];
  const b = baseAt(st, h);
  if (b && (b.hp ?? 0) > 0) return b.owner;
  for (const d of DIRS) {
    const n = add(h, d);
    if (st.oreOwner?.[key(n)]) return st.oreOwner[key(n)];
    const nb = baseAt(st, n);
    if (nb && (nb.hp ?? 0) > 0) return nb.owner;
  }
  return 0;
}
